/**
 * Zyft Analytics Tracker
 * Lightweight, privacy-first analytics tracker script.
 * Auto-initializes from data-site attribute on the script tag.
 */
export {};

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface ZyftEvent {
  event_name: string;
  url: string;
  url_path: string;
  referrer: string;
  referrer_domain: string;
  timestamp: string;
  session_id: string;
  visitor_id: string;
  screen_width: number;
  screen_height: number;
  device_type: string;
  user_agent: string;
  language: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  engagement_time_ms?: number;
  properties?: Record<string, string | number | boolean>;
}

interface ZyftConfig {
  siteKey: string;
  endpoint: string;
  scriptOrigin: string;
}

interface ReplayConfigResponse {
  enabled: boolean;
  sample_rate: number;
}

interface HeatmapConfigResponse {
  enabled: boolean;
  enableMouseMove?: boolean;
}

interface FormAnalyticsConfigResponse {
  enabled: boolean;
}

interface ZyftReplayModule {
  init: (config: {
    siteKey: string;
    sessionId: string;
    trackerEndpoint: string;
    sampleRate?: number;
  }) => void;
  stop: () => void;
}

interface ZyftInitOptions {
  respectDNT?: boolean;
}

const SESSION_KEY = "_zyft_sid";
const SESSION_TS_KEY = "_zyft_sts";
const UTM_KEY = "_zyft_utm";
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const FLUSH_INTERVAL = 500; // 500ms — fast enough for live view, still batches multiple events
const MAX_RETRIES = 3;

function getScriptConfig(): ZyftConfig | null {
  const scripts = document.querySelectorAll("script[data-site]");
  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i] as HTMLScriptElement;
    const siteKey = script.getAttribute("data-site");
    if (siteKey) {
      // Derive endpoint from script src or default to current origin
      const src = script.getAttribute("src") || "";
      let endpoint: string;
      let scriptOrigin: string;
      try {
        const url = new URL(src, window.location.origin);
        endpoint = url.origin + "/api/v1/track";
        scriptOrigin = url.origin;
      } catch {
        endpoint = window.location.origin + "/api/v1/track";
        scriptOrigin = window.location.origin;
      }
      return { siteKey, endpoint, scriptOrigin };
    }
  }
  return null;
}

function generateVisitorId(): string {
  // Daily rotating visitor ID based on browser fingerprint signals + date.
  // Rotates daily for privacy. Uses multiple signals to reduce collision
  // rate between users with identical browsers/screens.
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const raw =
    navigator.userAgent +
    screen.width +
    "x" +
    screen.height +
    screen.colorDepth +
    today +
    (navigator.language || "") +
    new Date().getTimezoneOffset() +
    (navigator.hardwareConcurrency || 0) +
    (navigator.maxTouchPoints || 0) +
    (navigator.platform || "");
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32-bit integer
  }
  // Convert to hex string padded to 16 chars
  const hex = (hash >>> 0).toString(16).padStart(8, "0");
  // Create a longer, more unique visitor ID by combining multiple hash rounds
  let hash2 = 0;
  for (let i = raw.length - 1; i >= 0; i--) {
    const chr = raw.charCodeAt(i);
    hash2 = (hash2 << 7) - hash2 + chr;
    hash2 |= 0;
  }
  const hex2 = (hash2 >>> 0).toString(16).padStart(8, "0");
  return hex + hex2;
}

// `activity` false: a page-hide report (engagement, web vitals) about the
// page the visitor already saw. Such an event neither starts a session nor
// extends one: after 30 idle minutes it used to open a session with no
// pageview in it (15% of all sessions), counted as an extra visit from the
// same referrer.
function getSessionId(activity = true): string {
  try {
    const now = Date.now();
    const storedTs = sessionStorage.getItem(SESSION_TS_KEY);
    const storedId = sessionStorage.getItem(SESSION_KEY);

    if (storedId && !activity) return storedId;

    // If session exists and hasn't timed out, reuse it
    if (storedId && storedTs) {
      const elapsed = now - parseInt(storedTs, 10);
      if (elapsed < SESSION_TIMEOUT) {
        sessionStorage.setItem(SESSION_TS_KEY, now.toString());
        return storedId;
      }
    }

    // Generate new session ID (UUID v4-like)
    const id = generateUUID();
    // A new session in a page that already identified (an SPA after 30 idle
    // minutes): queueEvent re-sends the identify into it. Not in the catch
    // path below, where every event gets a fresh id.
    if (lastUserId) reidentify = true;
    sessionStorage.setItem(SESSION_KEY, id);
    sessionStorage.setItem(SESSION_TS_KEY, now.toString());
    // Clear UTM params so new session can capture fresh ones
    sessionStorage.removeItem(UTM_KEY);
    return id;
  } catch {
    // sessionStorage may be blocked (e.g., incognito in some browsers)
    return generateUUID();
  }
}

function generateUUID(): string {
  // Simple UUID v4 generator
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function extractUtmFromUrl(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  for (let i = 0; i < UTM_PARAMS.length; i++) {
    const key = UTM_PARAMS[i];
    const val = params.get(key);
    if (val) {
      utm[key] = val;
    }
  }
  return utm;
}

function getSessionUtm(): UtmParams {
  // First touch within session: if UTM params already stored, use those
  try {
    const stored = sessionStorage.getItem(UTM_KEY);
    if (stored) {
      return JSON.parse(stored) as UtmParams;
    }
  } catch {
    // sessionStorage may be unavailable
  }

  // Extract from current URL and persist for session
  const utm = extractUtmFromUrl();
  if (Object.keys(utm).length > 0) {
    try {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
    } catch {
      // sessionStorage may be unavailable
    }
  }
  return utm;
}

function getDeviceType(): string {
  const width = screen.width;
  if (width < 768) return "mobile";
  if (width <= 1024) return "tablet";
  return "desktop";
}

function validateEventName(name: string): string | null {
  if (!name || typeof name !== "string") return null;
  const trimmed = name.slice(0, 100);
  // Only allow alphanumeric, underscore, and dash
  if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) return null;
  return trimmed;
}

function getReferrerDomain(referrer: string): string {
  if (!referrer) return "";
  try {
    return new URL(referrer).hostname;
  } catch {
    return "";
  }
}

// Strip a leading "www." so a www / non-www self-referral still matches.
function bareHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, "");
}

// True when the referrer host is this site itself (same registrable host).
// Self-referrals are internal navigation, not an external source — drop them
// at the source. The server applies the authoritative check (configured domain
// + subdomains); this just removes the common same-host noise early.
function isSelfReferrer(referrerHost: string): boolean {
  if (!referrerHost) return false;
  return bareHost(referrerHost) === bareHost(window.location.hostname);
}

function createEvent(
  eventName: string,
  properties?: Record<string, string | number | boolean>
): ZyftEvent {
  const loc = window.location;
  const utm = getSessionUtm();
  const referrerDomain = getReferrerDomain(document.referrer);
  const selfRef = isSelfReferrer(referrerDomain);
  return {
    event_name: eventName,
    url: loc.href,
    url_path: loc.pathname,
    referrer: selfRef ? "" : document.referrer,
    referrer_domain: selfRef ? "" : referrerDomain,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(eventName !== "engagement" && eventName !== "web_vitals"),
    visitor_id: generateVisitorId(),
    screen_width: screen.width,
    screen_height: screen.height,
    device_type: getDeviceType(),
    user_agent: navigator.userAgent,
    language: navigator.language || "",
    ...utm,
    ...(properties ? { properties } : {}),
  };
}

// --- Event Queue & Batching ---

let eventQueue: ZyftEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let trackerConfig: ZyftConfig | null = null;
let dntEnabled = false;

function isDNTActive(): boolean {
  return (
    navigator.doNotTrack === "1" ||
    (window as unknown as Record<string, string>).doNotTrack === "1"
  );
}

function scheduleFlush(): void {
  if (flushTimer !== null) return; // Already scheduled
  flushTimer = setTimeout(flushQueue, FLUSH_INTERVAL);
}

function flushQueue(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (!trackerConfig || eventQueue.length === 0) return;

  // Take all events from the queue
  const batch = eventQueue.splice(0);
  sendBatch(trackerConfig, batch, 0);
}

function sendBatch(config: ZyftConfig, events: ZyftEvent[], attempt: number): void {
  const payload = JSON.stringify({
    client_id: config.siteKey,
    events: events,
  });

  try {
    fetch(config.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      // Cross-origin analytics endpoint is cookieless. credentials:"omit" keeps
      // the request non-credentialed so the server's wildcard CORS header is valid
      // (a credentialed request + `Access-Control-Allow-Origin: *` is rejected).
      credentials: "omit",
      mode: "cors",
    })
      .then(function (res) {
        // Only a server-side or rate-limit failure can succeed on a resend;
        // a 4xx means the batch itself was refused and would be every time.
        if (
          !res.ok &&
          (res.status >= 500 || res.status === 429) &&
          attempt < MAX_RETRIES - 1
        ) {
          retryBatch(config, events, attempt);
        }
      })
      .catch(function () {
        if (attempt < MAX_RETRIES - 1) {
          retryBatch(config, events, attempt);
        }
        // After max retries, silently discard - never throw to host page
      });
  } catch {
    // Silently fail - tracking failures should never impact host page
  }
}

function retryBatch(config: ZyftConfig, events: ZyftEvent[], attempt: number): void {
  // Exponential backoff: 1s, 2s, 4s
  const delay = Math.pow(2, attempt) * 1000;
  setTimeout(function () {
    sendBatch(config, events, attempt + 1);
  }, delay);
}

function flushOnUnload(): void {
  if (!trackerConfig || eventQueue.length === 0) return;

  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  const batch = eventQueue.splice(0);

  // Use keepalive fetch (NOT navigator.sendBeacon). sendBeacon always sends with
  // credentials (cookies), and a credentialed cross-origin request is rejected by
  // the browser when the server returns `Access-Control-Allow-Origin: *`. A
  // keepalive fetch with credentials:"omit" survives page unload AND keeps the
  // request non-credentialed, so events sent on page-hide (e.g. Web Vitals,
  // buffered errors) actually reach the server cross-origin.
  try {
    fetch(trackerConfig.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: trackerConfig.siteKey,
        events: batch,
      }),
      keepalive: true,
      credentials: "omit",
      mode: "cors",
    }).catch(function () {
      // Silently fail — tracking must never impact the host page.
    });
  } catch {
    // Silently fail
  }
}

// The user id last passed to identify, in memory ONLY: sessionStorage would
// survive a logout and tag the next person using the same tab. Retention
// counts a session only when it contains an identify, so a new session in the
// same page gets an automatic one (auto: true; the users report ignores those
// for traits). zyft.reset() forgets it.
let lastUserId = "";
let reidentify = false;

function queueEvent(event: ZyftEvent): void {
  eventQueue.push(event);
  if (reidentify) {
    reidentify = false;
    eventQueue.push(createEvent("identify", { user_id: lastUserId, auto: true }));
  }
  scheduleFlush();
}

// --- Scroll Depth Tracking ---

let scrollDepthEnabled = false;
const SCROLL_THRESHOLDS = [25, 50, 75, 100];
let reportedThresholds: Set<number> = new Set();
let scrollTrackingPath = "";

function getScrollDepthPercent(): number {
  const windowHeight = window.innerHeight;
  const documentHeight = document.documentElement.scrollHeight;
  if (documentHeight <= windowHeight) return 100;
  const scrolled = window.scrollY + windowHeight;
  return Math.min(100, Math.round((scrolled / documentHeight) * 100));
}

function handleScroll(): void {
  if (!scrollDepthEnabled || !trackerConfig) return;
  if (dntEnabled && isDNTActive()) return;

  // Reset thresholds on page change (SPA)
  const currentPath = window.location.pathname;
  if (currentPath !== scrollTrackingPath) {
    reportedThresholds = new Set();
    scrollTrackingPath = currentPath;
  }

  const depth = getScrollDepthPercent();
  for (let i = 0; i < SCROLL_THRESHOLDS.length; i++) {
    const threshold = SCROLL_THRESHOLDS[i];
    if (depth >= threshold && !reportedThresholds.has(threshold)) {
      reportedThresholds.add(threshold);
      const event = createEvent("scroll", {
        depth: threshold,
        url_path: currentPath,
      });
      queueEvent(event);
    }
  }
}

let scrollTrackingSetup = false;
function setupScrollDepthTracking(): void {
  if (scrollTrackingSetup) return;
  scrollTrackingSetup = true;
  scrollTrackingPath = window.location.pathname;
  let scrollTicking = false;
  window.addEventListener("scroll", function () {
    if (!scrollTicking) {
      scrollTicking = true;
      requestAnimationFrame(function () {
        handleScroll();
        scrollTicking = false;
      });
    }
  }, { passive: true });
  // Also check initial position (page might already be scrolled or short page)
  handleScroll();
}

// --- Heatmap Click Tracking ---

let heatmapEnabled = false;
let lastClickTime = 0;
const CLICK_THROTTLE_MS = 100;

function getCssSelector(el: Element): string {
  if (el.id) return "#" + el.id;

  const parts: string[] = [];
  let current: Element | null = el;
  while (current && current !== document.body && current !== document.documentElement) {
    let selector = current.tagName.toLowerCase();

    if (current.id) {
      parts.unshift("#" + current.id);
      break;
    }

    if (current.className && typeof current.className === "string") {
      const classes = current.className.trim().split(/\s+/).slice(0, 2).join(".");
      if (classes) selector += "." + classes;
    }

    const parent: Element | null = current.parentElement;
    if (parent) {
      const siblings = parent.children;
      let idx = 0;
      let sameTagCount = 0;
      for (let i = 0; i < siblings.length; i++) {
        if (siblings[i].tagName === current.tagName) {
          sameTagCount++;
          if (siblings[i] === current) idx = sameTagCount;
        }
      }
      if (sameTagCount > 1) selector += ":nth-of-type(" + idx + ")";
    }

    parts.unshift(selector);
    current = parent;
  }

  return parts.join(" > ");
}

function getElementText(el: Element): string {
  const text = (el.textContent || "").trim();
  return text.slice(0, 50);
}

function handleHeatmapClick(e: MouseEvent): void {
  if (!heatmapEnabled || !trackerConfig) return;
  if (dntEnabled && isDNTActive()) return;

  const now = Date.now();
  if (now - lastClickTime < CLICK_THROTTLE_MS) return;
  lastClickTime = now;

  const target = e.target as Element | null;
  if (!target) return;

  const event = createEvent("click", {
    x: e.clientX,
    y: e.clientY,
    selector: getCssSelector(target),
    element_text: getElementText(target),
  });
  queueEvent(event);
}

let heatmapTrackingSetup = false;
function setupHeatmapTracking(): void {
  if (heatmapTrackingSetup) return;
  heatmapTrackingSetup = true;
  document.addEventListener("click", handleHeatmapClick, true);
}

// --- Heatmap Mouse Move Tracking ---

let mouseMoveEnabled = false;
let lastMoveTime = 0;
const MOVE_THROTTLE_MS = 200;

function handleMouseMove(e: MouseEvent): void {
  if (!mouseMoveEnabled || !trackerConfig) return;
  if (dntEnabled && isDNTActive()) return;

  const now = Date.now();
  if (now - lastMoveTime < MOVE_THROTTLE_MS) return;
  lastMoveTime = now;

  const event = createEvent("mousemove", {
    x: e.clientX,
    y: e.clientY,
  });
  queueEvent(event);
}

let mouseMoveTrackingSetup = false;
function setupMouseMoveTracking(): void {
  if (mouseMoveTrackingSetup) return;
  mouseMoveTrackingSetup = true;
  document.addEventListener("mousemove", handleMouseMove, { passive: true });
}

function fetchHeatmapConfig(config: ZyftConfig): void {
  const configUrl = config.scriptOrigin + "/api/v1/sites/" + config.siteKey + "/heatmap-config";
  try {
    fetch(configUrl, { method: "GET", credentials: "omit" })
      .then(function (res) {
        if (!res.ok) return;
        return res.json();
      })
      .then(function (data: unknown) {
        const heatmapData = data as HeatmapConfigResponse | undefined;
        if (heatmapData && heatmapData.enabled) {
          heatmapEnabled = true;
          setupHeatmapTracking();
          // Scroll depth tracking is enabled alongside heatmaps
          scrollDepthEnabled = true;
          setupScrollDepthTracking();
          // Mouse move tracking is opt-in (high data volume)
          if (heatmapData.enableMouseMove) {
            mouseMoveEnabled = true;
            setupMouseMoveTracking();
          }
        }
      })
      .catch(function () {
        // Silently fail - heatmap is optional
      });
  } catch {
    // Silently fail
  }
}

// --- Form Analytics Tracking ---

let formAnalyticsEnabled = false;
const fieldFocusTimes: Map<string, number> = new Map();

function getFieldIdentifier(el: HTMLElement): string {
  if (el.id) return el.id;
  const name = el.getAttribute("name");
  if (name) return name;
  // Fall back to type + index within form
  const form = (el as HTMLInputElement).form;
  if (form) {
    const fields = form.elements;
    for (let i = 0; i < fields.length; i++) {
      if (fields[i] === el) return el.tagName.toLowerCase() + "_" + i;
    }
  }
  return el.tagName.toLowerCase();
}

function getFormIdentifier(form: HTMLFormElement | null): string {
  if (!form) return "unknown";
  if (form.id) return form.id;
  const name = form.getAttribute("name");
  if (name) return name;
  const action = form.getAttribute("action");
  if (action) return action;
  return "form";
}

function isFormField(el: Element): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  const tag = el.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    const type = (el as HTMLInputElement).type;
    // Exclude hidden, submit, button, reset, image types
    return type !== "hidden" && type !== "submit" && type !== "button" && type !== "reset" && type !== "image";
  }
  return false;
}

function handleFieldFocus(e: FocusEvent): void {
  if (!formAnalyticsEnabled || !trackerConfig) return;
  if (dntEnabled && isDNTActive()) return;

  const target = e.target as HTMLElement | null;
  if (!target || !isFormField(target)) return;

  const fieldId = getFieldIdentifier(target);
  fieldFocusTimes.set(fieldId, Date.now());

  const formId = getFormIdentifier((target as HTMLInputElement).form);
  const event = createEvent("form_field_focus", {
    field_name: fieldId,
    field_type: (target as HTMLInputElement).type || target.tagName.toLowerCase(),
    form_id: formId,
    url_path: window.location.pathname,
  });
  queueEvent(event);
}

function handleFieldBlur(e: FocusEvent): void {
  if (!formAnalyticsEnabled || !trackerConfig) return;
  if (dntEnabled && isDNTActive()) return;

  const target = e.target as HTMLElement | null;
  if (!target || !isFormField(target)) return;

  const fieldId = getFieldIdentifier(target);
  const focusTime = fieldFocusTimes.get(fieldId);
  const durationMs = focusTime ? Date.now() - focusTime : 0;
  fieldFocusTimes.delete(fieldId);

  const formId = getFormIdentifier((target as HTMLInputElement).form);
  const event = createEvent("form_field_blur", {
    field_name: fieldId,
    field_type: (target as HTMLInputElement).type || target.tagName.toLowerCase(),
    form_id: formId,
    duration_ms: durationMs,
    url_path: window.location.pathname,
  });
  queueEvent(event);
}

function handleFormSubmit(e: Event): void {
  if (!formAnalyticsEnabled || !trackerConfig) return;
  if (dntEnabled && isDNTActive()) return;

  const form = e.target as HTMLFormElement;
  if (!form || form.tagName !== "FORM") return;

  const formId = getFormIdentifier(form);
  const event = createEvent("form_submit", {
    form_id: formId,
    url_path: window.location.pathname,
    status: "success",
  });
  queueEvent(event);
}

let formAnalyticsTrackingSetup = false;
function setupFormAnalyticsTracking(): void {
  if (formAnalyticsTrackingSetup) return;
  formAnalyticsTrackingSetup = true;
  document.addEventListener("focusin", handleFieldFocus, true);
  document.addEventListener("focusout", handleFieldBlur, true);
  document.addEventListener("submit", handleFormSubmit, true);
}

function fetchFormAnalyticsConfig(config: ZyftConfig): void {
  const configUrl = config.scriptOrigin + "/api/v1/sites/" + config.siteKey + "/form-analytics-config";
  try {
    fetch(configUrl, { method: "GET", credentials: "omit" })
      .then(function (res) {
        if (!res.ok) return;
        return res.json();
      })
      .then(function (data: unknown) {
        const formData = data as FormAnalyticsConfigResponse | undefined;
        if (formData && formData.enabled) {
          formAnalyticsEnabled = true;
          setupFormAnalyticsTracking();
        }
      })
      .catch(function () {
        // Silently fail - form analytics is optional
      });
  } catch {
    // Silently fail
  }
}

// --- Core Web Vitals (RUM) ---

let webVitalsReported = false;

// Shared "page is being left/hidden" hook: fires on tab-hide AND pagehide
// (Safari misses visibilitychange on some navigations; others skip pagehide).
// Callbacks must be idempotent — both can fire for one navigation.
function onPageHide(fn: () => void): void {
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") fn();
  });
  window.addEventListener("pagehide", fn);
}

function setupWebVitals(): void {
  if (typeof PerformanceObserver === "undefined") return;

  let lcp: number | null = null;
  let fcp: number | null = null;
  let cls = 0;
  let clsSession = 0;
  let clsFirstTs = 0;
  let clsLastTs = 0;
  let inp = 0;
  let inpSeen = false;

  function observe(
    type: string,
    cb: (entries: PerformanceEntry[]) => void,
    durationThreshold?: number
  ): void {
    try {
      const po = new PerformanceObserver(function (list) {
        cb(list.getEntries());
      });
      const init: PerformanceObserverInit = { type: type, buffered: true };
      if (durationThreshold !== undefined) {
        (
          init as PerformanceObserverInit & { durationThreshold?: number }
        ).durationThreshold = durationThreshold;
      }
      po.observe(init);
    } catch {
      // Entry type unsupported in this browser — skip this metric.
    }
  }

  // LCP: keep the latest candidate (finalized when we report on page hide).
  observe("largest-contentful-paint", function (entries) {
    const last = entries[entries.length - 1] as PerformanceEntry & {
      renderTime?: number;
      loadTime?: number;
    };
    if (last) {
      const t = last.renderTime || last.loadTime || last.startTime;
      if (t) lcp = t;
    }
  });

  // FCP from paint timing.
  observe("paint", function (entries) {
    for (let i = 0; i < entries.length; i++) {
      if (entries[i].name === "first-contentful-paint") {
        fcp = entries[i].startTime;
      }
    }
  });

  // CLS: session-window max (new window when gap > 1s or window spans > 5s).
  observe("layout-shift", function (entries) {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i] as PerformanceEntry & {
        value: number;
        hadRecentInput: boolean;
      };
      if (e.hadRecentInput) continue;
      const ts = e.startTime;
      if (
        clsSession !== 0 &&
        (ts - clsLastTs > 1000 || ts - clsFirstTs > 5000)
      ) {
        clsSession = 0;
      }
      if (clsSession === 0) clsFirstTs = ts;
      clsLastTs = ts;
      clsSession += e.value;
      if (clsSession > cls) cls = clsSession;
    }
  });

  // INP approximation: max interaction latency from Event Timing.
  observe(
    "event",
    function (entries) {
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i] as PerformanceEntry & { interactionId?: number };
        if (e.interactionId && e.duration > inp) {
          inp = e.duration;
          inpSeen = true;
        }
      }
    },
    40
  );
  observe("first-input", function (entries) {
    if (inpSeen) return;
    for (let i = 0; i < entries.length; i++) {
      if (entries[i].duration > inp) {
        inp = entries[i].duration;
        inpSeen = true;
      }
    }
  });

  function getTtfb(): number | null {
    try {
      const nav = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (nav && nav.responseStart > 0) return nav.responseStart;
    } catch {
      // ignore
    }
    return null;
  }

  function report(): void {
    if (webVitalsReported || !trackerConfig) return;
    if (dntEnabled && isDNTActive()) return;
    webVitalsReported = true;

    const props: Record<string, number> = {};
    if (lcp !== null) props.lcp = Math.round(lcp);
    if (fcp !== null) props.fcp = Math.round(fcp);
    if (inpSeen) props.inp = Math.round(inp);
    if (cls > 0) props.cls = Math.round(cls * 1000) / 1000;
    const ttfb = getTtfb();
    if (ttfb !== null) props.ttfb = Math.round(ttfb);

    // Don't send an empty event if nothing was collected.
    if (Object.keys(props).length === 0) return;

    queueEvent(createEvent("web_vitals", props));
    // Push-then-flush: guarantees the event is beaconed regardless of the order
    // in which unload listeners (here vs. setupUnloadHandlers) were registered.
    flushOnUnload();
  }

  onPageHide(report);
}

// --- Error Tracking ---

const reportedErrorFingerprints: Set<string> = new Set();
const MAX_ERRORS_PER_PAGE = 20;

function hashFingerprint(raw: string): string {
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return (hash >>> 0).toString(16);
}

// Strip ids/hex/numbers so the same error groups regardless of dynamic values.
function normalizeErrorMessage(message: string): string {
  return message
    .replace(/0x[0-9a-fA-F]+/g, "0x#")
    .replace(/\d+/g, "#")
    .slice(0, 200);
}

function firstStackFrame(stack: string): string {
  if (!stack) return "";
  const lines = stack.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim();
    if (l.indexOf("at ") === 0 || l.indexOf("@") >= 0) return l.slice(0, 200);
  }
  return (lines[1] || "").trim().slice(0, 200);
}

function reportError(
  kind: string,
  message: string,
  source: string,
  lineno: number,
  colno: number,
  stack: string
): void {
  try {
    if (!trackerConfig) return;
    if (dntEnabled && isDNTActive()) return;
    if (reportedErrorFingerprints.size >= MAX_ERRORS_PER_PAGE) return;

    // Ignore the tracker's own errors to avoid self-noise.
    if (
      (source && source.indexOf("/tracker.js") >= 0) ||
      (stack && stack.indexOf("/tracker.js") >= 0)
    ) {
      return;
    }

    const safeMessage = (message || "Unknown error").slice(0, 500);
    const safeStack = (stack || "").slice(0, 2000);
    const safeSource = (source || "").slice(0, 500);

    const fingerprint = hashFingerprint(
      kind +
        "\n" +
        normalizeErrorMessage(safeMessage) +
        "\n" +
        firstStackFrame(safeStack)
    );
    if (reportedErrorFingerprints.has(fingerprint)) return;
    reportedErrorFingerprints.add(fingerprint);

    const props: Record<string, string | number> = {
      fingerprint: fingerprint,
      kind: kind,
      message: safeMessage,
    };
    if (safeSource) props.source = safeSource;
    if (lineno) props.lineno = lineno;
    if (colno) props.colno = colno;
    if (safeStack) props.stack = safeStack;

    queueEvent(createEvent("web_error", props));
  } catch {
    // Never throw to the host page.
  }
}

function setupErrorTracking(): void {
  window.addEventListener("error", function (e: ErrorEvent) {
    try {
      // Ignore resource-load errors (img/script/link): they fire on the element
      // (nodeType 1) with an empty message, not on window.
      const target = e.target;
      if (target && target !== window && (target as Node).nodeType === 1) {
        return;
      }
      const err = e.error as Error | undefined;
      reportError(
        "error",
        e.message || (err && err.message) || "Unknown error",
        e.filename || "",
        e.lineno || 0,
        e.colno || 0,
        (err && err.stack) || ""
      );
    } catch {
      // swallow — never throw to host page
    }
  });

  window.addEventListener(
    "unhandledrejection",
    function (e: PromiseRejectionEvent) {
      try {
        const reason = e.reason;
        let message = "Unhandled promise rejection";
        let stack = "";
        if (reason instanceof Error) {
          message = reason.message || message;
          stack = reason.stack || "";
        } else if (typeof reason === "string") {
          message = reason;
        } else if (reason != null) {
          try {
            message = String(reason);
          } catch {
            // keep default message
          }
        }
        reportError("unhandledrejection", message, "", 0, 0, stack);
      } catch {
        // swallow — never throw to host page
      }
    }
  );
}

// --- Outbound Link & Download Tracking ---

const DOWNLOAD_EXTS = [
  "pdf", "zip", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv", "mp4",
];

function setupLinkTracking(): void {
  document.addEventListener(
    "click",
    function (e) {
      try {
        if (dntEnabled && isDNTActive()) return;
        // e.target may be a text node or SVG element inside the link —
        // resolve to an Element first so clicks on link text are captured.
        const t = e.target as Node | null;
        const el = t
          ? t.nodeType === 1
            ? (t as Element)
            : (t as Node).parentElement
          : null;
        const a = el && el.closest ? el.closest("a[href]") : null;
        if (!a) return;
        const href = a.getAttribute("href");
        if (!href) return;
        let url: URL;
        try {
          url = new URL(href, location.href);
        } catch {
          return;
        }
        const proto = url.protocol;
        // Phone / email links: count the click; never store the PII target.
        if (proto === "tel:" || proto === "mailto:") {
          queueEvent(
            createEvent("contact_click", {
              kind: proto === "tel:" ? "phone" : "email",
            })
          );
          return;
        }
        if (proto !== "http:" && proto !== "https:") return;
        const m = url.pathname.match(/\.([a-z0-9]{1,8})$/i);
        const ext = m ? m[1].toLowerCase() : "";
        if (a.hasAttribute("download") || (ext && DOWNLOAD_EXTS.indexOf(ext) >= 0)) {
          queueEvent(
            createEvent("file_download", {
              url: url.href,
              file: url.pathname.split("/").pop() || "",
              ext: ext,
            })
          );
        } else if (url.host !== location.host) {
          // WhatsApp click-to-chat links are outbound https; label them.
          if (/(^|\.)(wa\.me|whatsapp\.com)$/i.test(url.host)) {
            queueEvent(createEvent("contact_click", { kind: "whatsapp" }));
          } else {
            queueEvent(
              createEvent("outbound_click", { url: url.href, domain: url.host })
            );
          }
        }
      } catch {
        // Never throw to the host page.
      }
    },
    true
  );
}

// --- Declarative Event Tracking (data-zyft-event) ---
// One attribute on any element turns it into a trackable custom event:
//   <button data-zyft-event="newsletter_signup">  → fires on click
//   <form data-zyft-event="newsletter_signup">    → fires on submit
// No config fetch needed; always on. Reserved internal names are rejected so
// a declarative attribute can't pollute the instrumentation streams.

// Comma-delimited for compact matching; mirrors lib/analytics/reserved-events,
// plus "purchase": every purchase event counts as an order server-side, so the
// declarative path must not mint zero-revenue orders — zyft.revenue() is the
// sanctioned way to record purchases.
const RESERVED_NAMES =
  ",pageview,engagement,scroll,click,mousemove,web_vitals,web_error,outbound_click,file_download,contact_click,not_found,form_field_focus,form_field_blur,form_submit,identify,purchase,";

function readZyftEventName(el: Element): string | null {
  const name = validateEventName(el.getAttribute("data-zyft-event") || "");
  // Case-insensitive reserved check so "Pageview" can't slip past as a
  // confusing near-collision of the internal stream.
  if (!name || RESERVED_NAMES.indexOf("," + name.toLowerCase() + ",") >= 0) {
    return null;
  }
  return name;
}

function setupDeclarativeTracking(): void {
  document.addEventListener(
    "click",
    function (e) {
      try {
        if (!trackerConfig) return;
        if (dntEnabled && isDNTActive()) return;
        const t = e.target as Node | null;
        const el = t
          ? t.nodeType === 1
            ? (t as Element)
            : (t as Node).parentElement
          : null;
        const m = el && el.closest ? el.closest("[data-zyft-event]") : null;
        // Forms fire on submit only: closest() from any field/button inside a
        // tagged form would reach the form's attribute and fire on every
        // focus-click, flooding the stream and double-firing with submit.
        if (!m || m.tagName === "FORM") return;
        const name = readZyftEventName(m);
        if (!name) return;
        queueEvent(createEvent(name));
      } catch {
        // Never throw to the host page.
      }
    },
    true
  );

  document.addEventListener(
    "submit",
    function (e) {
      try {
        if (!trackerConfig) return;
        if (dntEnabled && isDNTActive()) return;
        const form = e.target as Element | null;
        if (!form || form.tagName !== "FORM") return;
        if (!form.getAttribute("data-zyft-event")) return;
        const name = readZyftEventName(form);
        if (!name) return;
        queueEvent(createEvent(name));
      } catch {
        // Never throw to the host page.
      }
    },
    true
  );
}

// --- CSS-selector Click Goals (GTM-style) ---
// The dashboard can define click goals as (CSS selector, event name) pairs;
// the tracker fetches them from the public config endpoint and emits the
// event when a click lands inside a matching element. No HTML changes and no
// prior data needed — works on day one of a new site.

interface ClickGoalPair {
  s: string;
  e: string;
}

function setupClickGoals(goals: ClickGoalPair[]): void {
  document.addEventListener(
    "click",
    function (e) {
      try {
        if (!trackerConfig) return;
        if (dntEnabled && isDNTActive()) return;
        const t = e.target as Node | null;
        const el = t
          ? t.nodeType === 1
            ? (t as Element)
            : (t as Node).parentElement
          : null;
        if (!el || !el.closest) return;
        // One emit per unique event name per click: goals sharing a name
        // would otherwise double-count events (conversions are deduped
        // per-goal/session, but raw event counts are not). Null prototype:
        // a plain {} would treat valid names like "constructor" as already
        // fired via the prototype chain and silently never emit them.
        const fired: Record<string, 1> = Object.create(null);
        for (let i = 0; i < goals.length; i++) {
          const g = goals[i];
          try {
            const m = el.closest(g.s);
            // Selectors matching a FORM would fire on every field click —
            // same trap the data-zyft-event click path guards against.
            if (!m || m.tagName === "FORM") continue;
            const name = validateEventName(g.e);
            if (!name || fired[name]) continue;
            fired[name] = 1;
            queueEvent(createEvent(name));
          } catch {
            // Invalid selector — skip this goal, keep checking the rest.
          }
        }
      } catch {
        // Never throw to the host page.
      }
    },
    true
  );
}

function fetchClickGoals(config: ZyftConfig): void {
  const configUrl =
    config.scriptOrigin + "/api/v1/sites/" + config.siteKey + "/click-goals-config";
  try {
    fetch(configUrl, { method: "GET", credentials: "omit" })
      .then(function (res) {
        if (!res.ok) return;
        return res.json();
      })
      .then(function (data: unknown) {
        const payload = data as { goals?: unknown } | undefined;
        if (!payload || !Array.isArray(payload.goals)) return;
        const goals: ClickGoalPair[] = [];
        for (let i = 0; i < payload.goals.length && goals.length < 20; i++) {
          const g = payload.goals[i] as ClickGoalPair;
          if (g && typeof g.s === "string" && typeof g.e === "string") {
            goals.push(g);
          }
        }
        if (goals.length > 0) setupClickGoals(goals);
      })
      .catch(function () {
        // Silently fail - click goals are optional
      });
  } catch {
    // Silently fail
  }
}

// --- Page Lifecycle ---

let unloadHandlersSetup = false;
// --- Engagement (active time-on-page) ---
// Accumulates the time the page is actually VISIBLE and reports it as an
// `engagement` event on page-hide / SPA nav, so the server can measure real
// engagement time — including single-page (bounce) sessions whose only other
// signal is the pageview. Idle/background time is excluded (the counter only runs
// while the tab is visible).
let engagementMs = 0;
let visibleSince: number | null = null;
// The path whose active time is currently accumulating. Pinned when a page's
// engagement begins (initial load + after an SPA nav) so the engagement event is
// tagged with the page being LEFT — not the destination. On History-API SPAs the
// URL has already changed by flush time, so reading window.location here would
// misattribute the leaving page's time to the next page.
let engagementPath: string | null = null;

// Accrue the open visible span, report engagement (>=1s) for the current page,
// reset, and resume counting if still visible. forceSend uses the keepalive
// unload flush so the event survives a page-hide.
function flushEngagement(forceSend: boolean): void {
  if (visibleSince !== null) {
    engagementMs += Date.now() - visibleSince;
    visibleSince = null;
  }
  if (engagementMs >= 1000 && trackerConfig && !(dntEnabled && isDNTActive())) {
    const e = createEvent("engagement");
    e.engagement_time_ms = engagementMs | 0;
    if (engagementPath) e.url_path = engagementPath;
    queueEvent(e);
    if (forceSend) flushOnUnload();
  }
  engagementMs = 0;
  if (document.visibilityState === "visible") visibleSince = Date.now();
}

function setupUnloadHandlers(): void {
  if (unloadHandlersSetup) return;
  unloadHandlersSetup = true;
  engagementPath = window.location.pathname;
  if (document.visibilityState === "visible") visibleSince = Date.now();
  onPageHide(function () {
    flushEngagement(true);
  });
  // Resume the visible-time clock when the tab becomes visible again.
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") visibleSince = Date.now();
  });
}

// --- Public API ---

function trackPageview(): void {
  if (dntEnabled && isDNTActive()) return;
  if (!trackerConfig) return;
  const event = createEvent("pageview");
  queueEvent(event);
  // A server-rendered 404 page can opt in by adding <meta name="zyft-404"> so
  // the pageview is also recorded as a not_found event. Put this meta ONLY on the
  // 404 template, never globally, or every SPA pageview would re-fire it. SPA
  // 404s call zyft.notFound() instead.
  if (document.querySelector('meta[name="zyft-404"]')) {
    queueEvent(createEvent("not_found"));
  }
}

// SPA support: listen for History API changes
let spaTrackingSetup = false;
function setupSPATracking(): void {
  if (spaTrackingSetup) return;
  spaTrackingSetup = true;

  let lastPath = window.location.pathname + window.location.search;

  // Intercept pushState and replaceState
  const origPushState = history.pushState;
  const origReplaceState = history.replaceState;

  history.pushState = function (...args) {
    origPushState.apply(this, args);
    onUrlChange();
  };

  history.replaceState = function (...args) {
    origReplaceState.apply(this, args);
    onUrlChange();
  };

  window.addEventListener("popstate", onUrlChange);

  function onUrlChange(): void {
    const newPath = window.location.pathname + window.location.search;
    if (newPath !== lastPath) {
      // Report the leaving page's engagement (batched, tagged with the old path)
      // before the new pageview, then re-point the accumulator at the new page.
      flushEngagement(false);
      engagementPath = window.location.pathname;
      lastPath = newPath;
      trackPageview();
    }
  }
}

// --- Replay Integration ---

function fetchReplayConfig(config: ZyftConfig): void {
  const configUrl = config.scriptOrigin + "/api/v1/sites/" + config.siteKey + "/replay-config";
  try {
    fetch(configUrl, { method: "GET", credentials: "omit" })
      .then(function (res) {
        if (!res.ok) return;
        return res.json();
      })
      .then(function (data: unknown) {
        const replayData = data as ReplayConfigResponse | undefined;
        if (replayData && replayData.enabled) {
          loadReplayModule(config, replayData.sample_rate);
        }
      })
      .catch(function () {
        // Silently fail - replay is optional
      });
  } catch {
    // Silently fail
  }
}

function loadReplayModule(config: ZyftConfig, sampleRate: number): void {
  // Check if replay module is already loaded
  const win = window as unknown as Record<string, unknown>;
  if (win.__zyft_replay) {
    initReplayModule(config, sampleRate);
    return;
  }

  const script = document.createElement("script");
  script.src = config.scriptOrigin + "/replay.js";
  script.async = true;
  script.onload = function () {
    initReplayModule(config, sampleRate);
  };
  script.onerror = function () {
    // Silently fail - replay is optional
  };
  document.head.appendChild(script);
}

function initReplayModule(config: ZyftConfig, sampleRate: number): void {
  const win = window as unknown as Record<string, unknown>;
  const replayModule = win.__zyft_replay as ZyftReplayModule | undefined;
  if (!replayModule || typeof replayModule.init !== "function") return;

  replayModule.init({
    siteKey: config.siteKey,
    sessionId: getSessionId(),
    trackerEndpoint: config.endpoint,
    sampleRate: sampleRate,
  });
}

// Auto-initialize
(function init() {
  const config = getScriptConfig();
  if (!config) return;

  trackerConfig = config;

  // Set up unload handlers for beacon/queue flushing
  setupUnloadHandlers();

  // Collect Core Web Vitals (reported on page hide)
  setupWebVitals();

  // Capture uncaught JS errors + unhandled promise rejections
  setupErrorTracking();

  // Capture outbound link clicks + file downloads
  setupLinkTracking();

  // Capture data-zyft-event clicks/submits (declarative custom events)
  setupDeclarativeTracking();

  // Track initial pageview
  trackPageview();

  // Set up SPA navigation tracking
  setupSPATracking();

  // Lazy-load replay recording and heatmap config after page load (non-blocking)
  const initPostLoad = function () {
    fetchReplayConfig(config);
    fetchHeatmapConfig(config);
    fetchFormAnalyticsConfig(config);
    fetchClickGoals(config);
  };
  if (document.readyState === "complete") {
    setTimeout(initPostLoad, 0);
  } else {
    window.addEventListener("load", function () {
      setTimeout(initPostLoad, 100);
    });
  }

  // Expose global API for custom event tracking
  (window as unknown as Record<string, unknown>)["zyft"] = {
    track: function (
      eventName: string,
      properties?: Record<string, string | number | boolean>
    ) {
      if (dntEnabled && isDNTActive()) return;
      const validated = validateEventName(eventName);
      if (!validated) return;
      const event = createEvent(validated, properties);
      queueEvent(event);
    },
    conversion: function (
      goalName: string,
      properties?: Record<string, string | number | boolean>
    ) {
      if (dntEnabled && isDNTActive()) return;
      const validated = validateEventName(goalName);
      if (!validated) return;
      const merged = { ...properties, _is_conversion: true };
      const event = createEvent(validated, merged as Record<string, string | number | boolean>);
      queueEvent(event);
    },
    revenue: function (
      amount: number,
      properties?: Record<string, string | number | boolean>
    ) {
      if (dntEnabled && isDNTActive()) return;
      if (typeof amount !== "number" || !isFinite(amount) || amount < 0) return;
      const merged = { ...properties, revenue: amount };
      const event = createEvent(
        "purchase",
        merged as Record<string, string | number | boolean>
      );
      queueEvent(event);
    },
    identify: function (
      userId: string,
      traits?: Record<string, string | number | boolean>
    ) {
      if (dntEnabled && isDNTActive()) return;
      if (!userId) return;
      const uid = String(userId).slice(0, 255);
      const merged = { ...traits, user_id: uid };
      const event = createEvent(
        "identify",
        merged as Record<string, string | number | boolean>
      );
      // This identify already covers the (possibly new) session.
      lastUserId = uid;
      reidentify = false;
      queueEvent(event);
    },
    // Call on logout: forget the user and end the session, so whatever
    // happens next in this tab (another person logging in) is not part of
    // the identified user's session. The session is expired, not removed:
    // the page-hide report of the page being left must still land in it
    // (with no stored id it would open a pageview-less session). A running
    // recording is stopped (flushed into the ended session); it resumes on
    // the next page load.
    reset: function () {
      lastUserId = "";
      reidentify = false;
      try {
        sessionStorage.setItem(SESSION_TS_KEY, "0");
      } catch {
        // sessionStorage may be unavailable
      }
      const replay = (window as unknown as Record<string, unknown>)
        .__zyft_replay as ZyftReplayModule | undefined;
      if (replay && typeof replay.stop === "function") replay.stop();
    },
    notFound: function () {
      if (dntEnabled && isDNTActive()) return;
      queueEvent(createEvent("not_found"));
    },
    init: function (options: ZyftInitOptions) {
      if (options && options.respectDNT) {
        dntEnabled = true;
        // If DNT is active after enabling respect, stop tracking
        if (isDNTActive()) {
          // Clear any queued events
          eventQueue = [];
          if (flushTimer !== null) {
            clearTimeout(flushTimer);
            flushTimer = null;
          }
        }
      }
    },
  };
})();
